import { useEffect, useRef, useState, type MouseEvent } from 'react';
import { HugeiconsIcon } from '@hugeicons/react';
import { ArrowDown01Icon, SourceCodeIcon,Maximize02Icon, PaintBoardIcon, Copy01Icon, Tick02Icon } from '@hugeicons/core-free-icons';
import type { Ticket } from '@overlayqa/shared';
import { CopyTooltip, ElementStyleDetails, elementStyleCopyLines } from '@overlayqa/shared/ui';
import '@overlayqa/shared/ui/styles.css';
import { log } from '@/lib/logger';
import { capture } from '../../lib/analytics';
import { copyText } from '@/lib/copyText';

interface ElementDataSectionProps {
  ticket: Ticket;
}

/**
 * Displays captured DOM element data and design tokens for an issue.
 * Only renders when elementData is present (element-pinned issues).
 */
export function ElementDataSection({ ticket }: ElementDataSectionProps) {
  const el = ticket.elementData;
  if (!el) return null;

  const tokens = el.designMetadata;
  const elementStyles = { evidence: el.styleCapture, measured: el.measuredComparison, comparison: !!el.comparisonContext,
    showUnavailable: !!(el.comparisonContext || el.autoReview) };
  const tokenEntries = tokens
    ? Object.entries(tokens).filter(
        (entry): entry is [string, { value: string; cssToken: string | null; figmaToken: string | null; tokenFound: boolean }] =>
          entry[1] != null && typeof entry[1] === 'object' && !!(entry[1].value || entry[1].cssToken || entry[1].figmaToken)
      )
    : [];

  log.info('[ElementDataSection] Rendering', { selector: el.selector, tokenCount: tokenEntries.length });
  const detailsText = [
    'Element Details',
    ...(el.className ? [`Class: ${el.className}`] : []),
    ...(el.selector ? [`Selector: ${el.selector}`] : []),
    ...(el.boundingRect ? [`Position & Size: ${el.boundingRect.width} x ${el.boundingRect.height}px at (${Math.round(el.boundingRect.x)}, ${Math.round(el.boundingRect.y)})`] : []),
    ...tokenEntries.filter(([, token]) => token.value).map(([prop, token]) =>
      `${formatPropName(prop)}: ${token.value}${token.cssToken ? ` (CSS: ${token.cssToken})` : ''}${token.figmaToken ? ` (Figma: ${token.figmaToken})` : ''}`),
    ...elementStyleCopyLines(elementStyles),
  ].join('\n');

  return (
    // Open by default (Emily, 2026-09-29); key resets it per issue. The native
    // marker is replaced by the SelectDropdown chevron (ArrowDown01Icon, 12px,
    // gray-400, rotate-180 when open).
    <details key={ticket.id} id="issue-element" className="card-sm group" open>
      <summary className="section-label cursor-pointer list-none [&::-webkit-details-marker]:hidden flex items-center gap-2">
        Element Details
        <HugeiconsIcon
          icon={ArrowDown01Icon}
          size={12}
          strokeWidth={2}
          color="currentColor"
          className="w-3 h-3 text-gray-400 flex-shrink-0 transition-transform group-open:rotate-180"
          aria-hidden="true"
        />
        <span className="ml-auto flex"><CopyValueButton value={detailsText} label="Element details" /></span>
      </summary>

      <div id="element-details" className="space-y-6 mt-4">
        <div className="oq-auto-review-ui"><ElementStyleDetails {...elementStyles} onEvent={(name, props) => capture(name, { ...props, surface: 'dashboard' })} /></div>
        {/* Class */}
        {el.className && (
          <div className="text-sm">
            <dt className="text-gray-500 text-xs mb-1 flex items-center gap-2 font-medium uppercase tracking-wide border-b border-gray-100 pb-1">
              <HugeiconsIcon icon={SourceCodeIcon} size={12} strokeWidth={2} color="currentColor" className="w-3 h-3" aria-hidden="true" />
              Class
            </dt>
            <dd className="font-mono text-xs bg-gray-50 rounded px-2 py-2 text-gray-700 flex items-center gap-2">
              <span className="break-all flex-1 min-w-0">{el.className}</span>
              <CopyValueButton value={el.className} label="Class" />
            </dd>
          </div>
        )}

        {/* Selector */}
          <div className="text-sm">
            <dt className="text-gray-500 text-xs mb-1 flex items-center gap-2 font-medium uppercase tracking-wide border-b border-gray-100 pb-1">
              <HugeiconsIcon icon={SourceCodeIcon} size={12} strokeWidth={2} color="currentColor" className="w-3 h-3" aria-hidden="true" />
              Selector
            </dt>
            <dd className="font-mono text-xs bg-gray-50 rounded px-2 py-2 text-gray-700 flex items-center gap-2">
              <span className="break-all flex-1 min-w-0">{el.selector}</span>
              <CopyValueButton value={el.selector} label="Selector" />
            </dd>
          </div>

          {/* Bounding rect */}
          {el.boundingRect && (el.boundingRect.width > 0 || el.boundingRect.height > 0) && (
            <div className="text-sm">
              <dt className="text-gray-500 text-xs mb-1 flex items-center gap-2 font-medium uppercase tracking-wide border-b border-gray-100 pb-1">
                <HugeiconsIcon icon={Maximize02Icon} size={12} strokeWidth={2} color="currentColor" className="w-3 h-3" aria-hidden="true" />
                Position & Size
              </dt>
              <dd className="font-mono text-xs text-gray-700">
                {el.boundingRect.width} x {el.boundingRect.height}px at ({Math.round(el.boundingRect.x)}, {Math.round(el.boundingRect.y)})
              </dd>
            </div>
          )}

          {/* Only populated legacy metadata supplements the captured values. */}
          {tokenEntries.length > 0 && <div>
            <dt className="text-gray-500 text-xs mb-2 flex items-center gap-2 font-medium uppercase tracking-wide border-b border-gray-100 pb-1">
              <HugeiconsIcon icon={PaintBoardIcon} size={12} strokeWidth={2} color="currentColor" className="w-3 h-3" aria-hidden="true" />
              Design Tokens
            </dt>
            <dd>
                <dl className="space-y-2">
                  {tokenEntries.map(([prop, token]) => (
                    <div key={prop} className="flex items-start gap-2 text-xs">
                      <span className="text-gray-500 w-24 flex-shrink-0 capitalize">
                        {formatPropName(prop)}
                      </span>
                      <span className={`font-mono flex-1 ${token.value ? 'text-gray-900' : 'text-gray-400 italic'}`}>
                        {token.value || 'Not set'}
                      </span>
                      {token.cssToken && (
                        <span className="font-mono text-primary-600 bg-primary-50 px-2 py-1 rounded text-[10px]">
                          {token.cssToken}
                        </span>
                      )}
                      {token.figmaToken && (
                        <span className="font-mono text-purple-600 bg-purple-50 px-2 py-1 rounded text-[10px]">
                          {token.figmaToken}
                        </span>
                      )}
                      {token.tokenFound === false && token.value && (
                        <span className="text-amber-600 bg-amber-50 px-2 py-1 rounded text-[10px]">
                          no token
                        </span>
                      )}
                    </div>
                  ))}
                </dl>
            </dd>
          </div>}
      </div>
    </details>
  );
}

/** Far-right copy icon for a code-value field (Class / Selector). */
function CopyValueButton({ value, label }: { value: string; label: string }) {
  const [copyState, setCopyState] = useState<'idle' | 'copied' | 'failed'>('idle');
  const timer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(timer.current), []);
  const copied = copyState === 'copied';
  const failed = copyState === 'failed';
  if (!value) return null;

  const handleCopy = async (event: MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    clearTimeout(timer.current);
    setCopyState('idle');
    // copyText resolves a boolean so this caller shows success OR failure
    // (its OQA-11 doc); branching only on success left failures invisible.
    const ok = await copyText(value);
    setCopyState(ok ? 'copied' : 'failed');
    timer.current = setTimeout(() => setCopyState('idle'), 1500);
  };

  return (
    <CopyTooltip label={`Copy ${label.toLowerCase()}`} copied={copied} failed={failed}>
    <button
      type="button"
      onClick={handleCopy}
      className="flex-shrink-0 -my-1 p-1 rounded text-gray-400 hover:text-gray-700 hover:bg-gray-100 transition-colors"
      aria-label={copied ? `${label} copied` : failed ? `${label} copy failed` : `Copy ${label.toLowerCase()}`}
    >
      <HugeiconsIcon
        icon={copied ? Tick02Icon : Copy01Icon}
        size={14}
        strokeWidth={2}
        color="currentColor"
        className={`w-3.5 h-3.5 ${copied ? 'text-green-600' : failed ? 'text-red-600' : ''}`}
        aria-hidden="true"
      />
    </button>
    </CopyTooltip>
  );
}

function formatPropName(name: string): string {
  return name.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase());
}
